import React, { useEffect, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { useHistory } from "react-router-dom";
import { lang } from "../../../middleware/lang";
import { LoaderDots } from "../../../middleware/loaders";
import { HeaderTitle } from "../common/titles";
import { has_access } from "../../../middleware/privileges";
import { GET_PURCHASE_ORDERS, GET_SUGGESTED_PURCHASE } from "./queries";
import { PurchaseOrder, PO_STATUS_LABELS, SuggestedPurchase } from "./models";
import InventoryNav from "./inventoryNav";

const btn = {
  txt: lang("Nueva Orden", "New Order"),
  icon: "fa fa-plus",
  link: "/inventory/purchases/new",
};

const PurchaseOrderList: React.FC = () => {
  const history = useHistory();
  const [statusFilter, setStatusFilter] = useState("");
  const [showSuggested, setShowSuggested] = useState(false);

  const [fetchOrders, { data, loading }] = useLazyQuery(GET_PURCHASE_ORDERS, {
    fetchPolicy: "no-cache",
  });
  const [fetchSuggested, { data: suggestedData, loading: suggestedLoading }] =
    useLazyQuery(GET_SUGGESTED_PURCHASE, { fetchPolicy: "no-cache" });

  useEffect(() => {
    fetchOrders({
      variables: {
        status: statusFilter || undefined,
        take: 100,
      },
    });
    //eslint-disable-next-line
  }, [statusFilter]);

  const formatDate = (timestamp: string | null) => {
    if (!timestamp) return "—";
    try {
      const date = new Date(parseInt(timestamp));
      return date.toLocaleDateString("es-MX", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return timestamp;
    }
  };

  const handleShowSuggested = () => {
    setShowSuggested(true);
    fetchSuggested();
  };

  const suggested: SuggestedPurchase[] =
    suggestedData?.getSuggestedPurchase || [];

  return (
    <>
      <HeaderTitle
        title={lang("Órdenes de Compra", "Purchase Orders")}
        btn={has_access(256) ? btn : null}
      />
      <InventoryNav active="purchases" />

      <div className="row mb-3">
        <div className="col-lg-3">
          <select
            className="custom-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">{lang("Todos los estados", "All statuses")}</option>
            {Object.entries(PO_STATUS_LABELS).map(([value, info]) => (
              <option key={value} value={value}>
                {lang(info.label, info.labelEn)}
              </option>
            ))}
          </select>
        </div>
        <div className="col-lg-3">
          <button
            className="btn btn-outline-info"
            onClick={handleShowSuggested}
          >
            <i className="fas fa-lightbulb"></i>{" "}
            {lang("Compra Sugerida", "Suggested Purchase")}
          </button>
        </div>
      </div>

      {/* Suggested purchase modal */}
      {showSuggested && (
        <div className="row mb-3">
          <div className="col-lg-12">
            <div className="material_card p-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h5 className="mb-0">
                  <i className="fas fa-lightbulb text-warning mr-2"></i>
                  {lang("Compra Sugerida", "Suggested Purchase")}
                </h5>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setShowSuggested(false)}
                >
                  <i className="fa fa-times"></i>
                </button>
              </div>
              {suggestedLoading && <LoaderDots fullSize />}
              {suggested.length === 0 && !suggestedLoading && (
                <p className="text-muted">
                  {lang(
                    "No hay ingredientes por debajo del punto de reorden",
                    "No ingredients below reorder point"
                  )}
                </p>
              )}
              {suggested.length > 0 && (
                <div className="table-responsive">
                  <table className="table table-sm">
                    <thead className="thead-light">
                      <tr>
                        <th>{lang("Ingrediente", "Ingredient")}</th>
                        <th>{lang("Stock actual", "Current stock")}</th>
                        <th>{lang("Punto reorden", "Reorder point")}</th>
                        <th>{lang("Cant. sugerida", "Suggested qty")}</th>
                        <th>{lang("Proveedor", "Supplier")}</th>
                        <th>{lang("Precio/U", "Price/U")}</th>
                        <th>{lang("Días", "Days")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {suggested.map((s, i) => (
                        <tr key={i}>
                          <td className="font-weight-bold">
                            {s.ingredient.name}
                          </td>
                          <td className="text-danger">
                            {Number(s.current_stock).toFixed(2)}{" "}
                            {s.ingredient.unit}
                          </td>
                          <td>{Number(s.reorder_point).toFixed(2)}</td>
                          <td className="font-weight-bold">
                            {Number(s.suggested_qty).toFixed(2)}
                          </td>
                          <td>{s.preferred_supplier?.name || "—"}</td>
                          <td>
                            {s.unit_price
                              ? `$${Number(s.unit_price).toFixed(2)}`
                              : "—"}
                          </td>
                          <td>{s.lead_time_days || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {loading && <LoaderDots fullSize />}

      {data && (
        <div className="row">
          <div className="col-lg-12">
            <div className="material_card p-3 table-responsive">
              <table className="table table-hover vertical_middle">
                <thead className="thead-light">
                  <tr>
                    <th>#</th>
                    <th>{lang("Proveedor", "Supplier")}</th>
                    <th>{lang("Sucursal", "Branch")}</th>
                    <th>{lang("Fecha", "Date")}</th>
                    <th>{lang("Entrega esperada", "Expected delivery")}</th>
                    <th>{lang("Estado", "Status")}</th>
                    <th>{lang("Total", "Total")}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.getPurchaseOrders.map((po: PurchaseOrder) => {
                    const statusInfo = PO_STATUS_LABELS[po.status] || {
                      label: po.status,
                      labelEn: po.status,
                      color: "secondary",
                    };
                    return (
                      <tr
                        key={po.id}
                        className="cursor-pointer"
                        onClick={() =>
                          history.push(`/inventory/purchases/${po.id}`)
                        }
                        style={{ cursor: "pointer" }}
                      >
                        <td className="font-weight-bold">OC-{po.id}</td>
                        <td>{po.supplier?.name || "—"}</td>
                        <td>
                          <small>{po.branch?.name || "—"}</small>
                        </td>
                        <td>
                          <small>{formatDate(po.created_at)}</small>
                        </td>
                        <td>
                          <small>{formatDate(po.expected_date)}</small>
                        </td>
                        <td>
                          <span className={`badge badge-${statusInfo.color}`}>
                            {lang(statusInfo.label, statusInfo.labelEn)}
                          </span>
                        </td>
                        <td className="font-weight-bold">
                          {po.total
                            ? `$${Number(po.total).toFixed(2)}`
                            : "—"}
                        </td>
                      </tr>
                    );
                  })}
                  {data.getPurchaseOrders.length === 0 && (
                    <tr>
                      <td colSpan={7} className="text-center text-muted py-4">
                        {lang(
                          "No hay órdenes de compra",
                          "No purchase orders"
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PurchaseOrderList;
